import { useEffect, useState } from "react";
import { useNavigate } from "react-router";
import { completeSignInFromLink, isSignInLink } from "@/lib/firebase";
import { useQueryClient } from "@tanstack/react-query";

/** Same key SignInPage writes to. Owning it in two places is fine — this
 *  is the only consumer, and inlining avoids cross-route imports. */
const SIGNIN_NEXT_KEY = "cura-studio:signin-next";

/**
 * Recover and validate the saved post-signin redirect. Always clears the
 * key so a stale value from a previous attempt can't pin the user to a
 * dead path forever. Only same-origin absolute paths are honoured.
 */
function consumeSignInNext(): string | null {
  try {
    const raw = window.localStorage.getItem(SIGNIN_NEXT_KEY);
    window.localStorage.removeItem(SIGNIN_NEXT_KEY);
    if (!raw) return null;
    if (!raw.startsWith("/") || raw.startsWith("//")) return null;
    return raw;
  } catch {
    return null;
  }
}

/**
 * Catches the email-link redirect, exchanges it for a Firebase session,
 * invalidates queries (which were possibly using the dev-bypass) and
 * lands the user on the catalogue.
 */
export function AuthFinishPage() {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [status, setStatus] = useState<"working" | "need-email" | "error">("working");
  const [error, setError] = useState<string | null>(null);
  const [email, setEmail] = useState("");

  useEffect(() => {
    const href = window.location.href;
    if (!isSignInLink(href)) {
      setStatus("error");
      setError("This URL isn't a valid sign-in link. Request a new one.");
      return;
    }
    completeSignInFromLink(href)
      .then(() => {
        // Drop the dev persona — Firebase identity wins from here.
        window.localStorage.removeItem("cura-studio:dev-user");
        qc.invalidateQueries();
        // Resume-after-signin: if the user had been bounced off a deep
        // link, land them back there. Otherwise fall through to "/" so
        // the landing page can route by role (admin → /admin/clients).
        const next = consumeSignInNext();
        navigate(next ?? "/", { replace: true });
      })
      .catch((err) => {
        if ((err as Error).message.toLowerCase().includes("email")) {
          setStatus("need-email");
        } else {
          setStatus("error");
          setError((err as Error).message);
        }
      });
  }, [navigate, qc]);

  if (status === "working") {
    return (
      <div className="mx-auto max-w-md px-6 py-16 text-center text-fg-3">
        Signing you in…
      </div>
    );
  }

  if (status === "need-email") {
    return (
      <div className="mx-auto max-w-md px-6 py-16">
        <h1 className="m-0 mb-2 text-center text-xl font-semibold">Confirm your email</h1>
        <p className="m-0 mb-6 text-center text-fg-3">
          The sign-in link was opened in a different browser. Re-enter the email you
          requested the link with.
        </p>
        <form
          onSubmit={async (e) => {
            e.preventDefault();
            try {
              await completeSignInFromLink(window.location.href, email);
              window.localStorage.removeItem("cura-studio:dev-user");
              qc.invalidateQueries();
              const next = consumeSignInNext();
              navigate(next ?? "/", { replace: true });
            } catch (err) {
              setStatus("error");
              setError((err as Error).message);
            }
          }}
          className="flex flex-col gap-3"
        >
          <input
            type="email"
            required
            placeholder="you@yourpractice.com"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            className="field-input"
          />
          <button
            type="submit"
            className="rounded-md px-4 py-3 text-sm font-semibold text-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--border-focus)]"
            style={{ background: "var(--studio)" }}
          >
            Finish sign-in
          </button>
        </form>
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-md px-6 py-16 text-center">
      <h1 className="m-0 mb-2 text-xl font-semibold text-fg-1">Sign-in failed</h1>
      <p className="m-0 mb-4 text-fg-3">{error}</p>
      <a href="/auth/signin" className="nhsuk-button nhsuk-button--secondary">
        Try again
      </a>
    </div>
  );
}
